import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { AuthService, ShellConfigService, TokenStorageService } from 'continity-shared-shell';

/**
 * A Phoenix-Run/Config `DataChanged` push, as broadcast by the portal's
 * `SignalRDataChangeNotifier` (PBI 1482 / Task 1483) to the `company-{tenantId}` group.
 * Mirrors the backend's anonymous payload `{ entityType, changeType, entityId, userId, timestamp }`.
 */
export interface PhoenixDataChange {
  /** `PhoenixRun` | `PhoenixConfig` (the board filters on the `Phoenix` prefix). */
  entityType: string;
  changeType: 'Created' | 'Updated' | 'Deleted' | 'Restored';
  /** The run's / config's stream id. */
  entityId: string;
  /** The user who caused the change (for client-side self-change filtering, if needed). */
  userId: string | null;
  timestamp: Date;
}

/**
 * Lean SignalR client for the standalone Phoenix-Portal-App (Task 1484).
 *
 * WHY ITS OWN CLIENT: the standalone app is deliberately decoupled from the main portal's
 * `SignalRService` (which is wired into the portal's auth/notification/sample-data graph).
 * This client mirrors only the slice the board needs — connect to the SAME NotificationHub,
 * join the caller's company group, and surface the `DataChanged` stream — and authenticates
 * over the SAME cookie/bearer seam as `auth.interceptor` (the in-memory bearer from
 * `TokenStorageService`, exactly what the interceptor attaches to `/api/` calls).
 *
 * Connection lifecycle is owned here: automatic reconnect with a capped backoff, re-join of
 * the company group after a reconnect (server drops group membership on reconnect), and a
 * `reconnected$` signal so the board can do a full reconcile after any gap. {@link stop} (and
 * {@link ngOnDestroy}) tear the connection down cleanly — no leaked sockets. Start/stop are safe
 * under a rapid re-mount: a {@link start} that races a still-running {@link stop} awaits that
 * teardown first, so the board reliably ends up with a fresh connection.
 *
 * EXTRACTION NOTE: the auth/config seam now comes from `continity-shared-shell`
 * (ShellConfigService + TokenStorageService). This SignalR client itself still lives in
 * the Phoenix app; a portal-shared SignalR client moving into the shell is a follow-up
 * (ADO #1522 remains open, incl. the MAIN-portal migration onto the shared shell).
 */
@Injectable({ providedIn: 'root' })
export class PhoenixSignalRService implements OnDestroy {
  private hub: signalR.HubConnection | null = null;

  private readonly connectionState$ =
    new BehaviorSubject<signalR.HubConnectionState>(signalR.HubConnectionState.Disconnected);
  private readonly dataChanges$ = new Subject<PhoenixDataChange>();
  /** Fires after a successful (re)connect AND company-group (re)join — the board uses it to
   *  full-refresh and heal any updates missed during the disconnect. */
  private readonly reconnected$ = new Subject<void>();

  /** In-flight {@link stop} teardown, if any. A {@link start} during teardown awaits this first
   *  so a rapid re-mount (board→runs→board) reliably gets a fresh connection rather than no-opping
   *  against a hub still in `Disconnecting`. */
  private stopInFlight: Promise<void> | null = null;

  private static readonly MAX_RECONNECT_ATTEMPTS = 6;
  private static readonly BASE_RECONNECT_DELAY_MS = 5_000;

  constructor(
    private config: ShellConfigService,
    private tokenStorage: TokenStorageService,
    private auth: AuthService
  ) {}

  /** Stream of Phoenix `DataChanged` pushes (already filtered to `entityType` `Phoenix*`). */
  get dataChanges(): Observable<PhoenixDataChange> {
    return this.dataChanges$.asObservable();
  }

  /** Emits after each successful (re)connect + company-group (re)join. */
  get reconnected(): Observable<void> {
    return this.reconnected$.asObservable();
  }

  get connectionState(): Observable<signalR.HubConnectionState> {
    return this.connectionState$.asObservable();
  }

  get isConnected(): boolean {
    return this.hub?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Starts the hub connection and joins the caller's company group. Idempotent: a second call
   * while already connected/connecting is a no-op. Requires a valid bearer (same precondition
   * as any `/api/` call); without one it throws, mirroring the main portal's client.
   */
  async start(): Promise<void> {
    // A rapid re-mount (board→runs→board) can call start() while the previous stop() is still
    // tearing the hub down (state `Disconnecting`). Await that teardown first, otherwise the
    // idempotency guard below would see a non-Disconnected hub and no-op, and stop()'s finally
    // would then null the hub — leaving the connection permanently down until the 60s poll.
    if (this.stopInFlight) {
      await this.stopInFlight;
    }

    if (this.hub && this.hub.state !== signalR.HubConnectionState.Disconnected) {
      return; // already connected / connecting
    }

    const token = this.tokenStorage.getToken();
    if (!token) {
      throw new Error('[PhoenixSignalR] No bearer token available — cannot start hub connection.');
    }

    // The hub lives on the API host, NOT under /api — strip a trailing /api the same way the
    // main portal's SignalRService does, so we hit `{host}/hubs/notification`.
    const apiUrl = this.config.apiUrl;
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;

    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/notification`, {
        // SignalR re-invokes this on EVERY (re)negotiate; the in-memory bearer is short-lived
        // so by reconnect time it may have expired. Refresh-or-throw — NEVER return '' — because
        // an empty string makes SignalR send `Authorization: Bearer ` (no token), which the API
        // rejects as a malformed JWT ("IDX14100 … there are no dots") and logs as an auth failure
        // on every retry transport. See {@link resolveBearer}.
        accessTokenFactory: () => this.resolveBearer(),
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => {
          if (ctx.previousRetryCount >= PhoenixSignalRService.MAX_RECONNECT_ATTEMPTS) {
            return null; // give up; the board's reconciliation poll remains the safety net
          }
          return Math.min(
            PhoenixSignalRService.BASE_RECONNECT_DELAY_MS * 2 ** ctx.previousRetryCount,
            30_000
          );
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.registerHandlers();

    await this.hub.start();
    this.connectionState$.next(this.hub.state);
    await this.joinCompanyGroup();
    this.reconnected$.next(); // initial join also warrants a board refresh
  }

  /**
   * Resolves the bearer SignalR attaches to each (re)negotiate. The in-memory bearer is
   * short-lived and SignalR re-invokes the factory on every reconnect, so it may have
   * expired since {@link start}. Returns the current bearer when still valid; otherwise
   * refreshes it from the HttpOnly cookie (mirroring the interceptor's seam) before
   * (re)connecting. If the refresh also fails (truly logged out), it THROWS so SignalR
   * aborts the attempt — never sending an empty `Bearer ` that the API would reject as a
   * malformed JWT and log on every retry. The board's reconciliation poll is the safety net.
   */
  private async resolveBearer(): Promise<string> {
    const current = this.tokenStorage.getToken();
    if (current) return current;

    // refreshToken() swallows its own errors → null on failure (never rejects).
    const refreshed = await firstValueFrom(this.auth.refreshToken());
    if (refreshed?.accessToken) return refreshed.accessToken;

    throw new Error(
      '[PhoenixSignalR] No valid bearer (cookie refresh failed) — aborting hub auth.'
    );
  }

  /** Tears the connection down. Safe to call when never started. A concurrent {@link start}
   *  (rapid re-mount) awaits the returned teardown before reconnecting. */
  async stop(): Promise<void> {
    if (!this.hub) return;
    // Reuse an already-running teardown so a re-mount awaits a single, consistent promise.
    if (this.stopInFlight) return this.stopInFlight;

    const hub = this.hub;
    this.stopInFlight = (async () => {
      try {
        if (hub.state !== signalR.HubConnectionState.Disconnected) {
          await hub.stop();
        }
      } catch (err) {
        // Best-effort teardown — log, never throw (the app is likely tearing down).
        console.warn('[PhoenixSignalR] Error stopping hub connection:', err);
      } finally {
        this.connectionState$.next(signalR.HubConnectionState.Disconnected);
        this.hub = null;
        this.stopInFlight = null;
      }
    })();
    return this.stopInFlight;
  }

  ngOnDestroy(): void {
    void this.stop();
  }

  private registerHandlers(): void {
    if (!this.hub) return;

    this.hub.onreconnecting(() =>
      this.connectionState$.next(signalR.HubConnectionState.Reconnecting)
    );

    this.hub.onreconnected(async () => {
      this.connectionState$.next(signalR.HubConnectionState.Connected);
      // Group membership is dropped on reconnect — re-join, then signal a full refresh.
      await this.joinCompanyGroup();
      this.reconnected$.next();
    });

    this.hub.onclose((err) => {
      if (err) {
        console.warn('[PhoenixSignalR] Hub connection closed with error:', err.message);
      }
      this.connectionState$.next(signalR.HubConnectionState.Disconnected);
    });

    this.hub.on('DataChanged', (raw: unknown) => {
      try {
        const n = raw as Record<string, unknown>;
        const entityType = String(n['entityType'] ?? '');
        // Task 1484 filter: this board only cares about Phoenix entities. Other tenants'
        // entity types (Contact, Process, …) share the same hub/group and are ignored.
        if (!entityType.startsWith('Phoenix')) return;

        this.dataChanges$.next({
          entityType,
          changeType: n['changeType'] as PhoenixDataChange['changeType'],
          entityId: String(n['entityId'] ?? ''),
          userId: (n['userId'] as string | null) ?? null,
          timestamp: new Date(n['timestamp'] as string),
        });
      } catch (err) {
        // Best-effort: a malformed push must not kill the subscription.
        console.warn('[PhoenixSignalR] Failed to process DataChanged payload:', err);
      }
    });
  }

  /**
   * Joins the caller's own company group. Uses `JoinMyCompanyGroup` (server resolves tenant_id
   * from the JWT) to avoid any race with stale frontend tenant data — the same approach as the
   * main portal's client.
   */
  private async joinCompanyGroup(): Promise<void> {
    if (this.hub?.state !== signalR.HubConnectionState.Connected) return;
    try {
      await this.hub.invoke('JoinMyCompanyGroup');
    } catch (err) {
      // Best-effort: without the group join, live push is silent but the reconciliation poll
      // still keeps the board consistent. Log so the dead-end is diagnosable.
      console.warn('[PhoenixSignalR] Failed to join company group:', err);
    }
  }
}
